import React, { useEffect, useRef } from 'react';

export interface TrackerJsProps {
  customJs?: string;
  location?: 'body' | 'head';
  trackerName?: string;
  forceHeadOnly?: boolean;
}

const JAVASCRIPT_TYPE =
  /^(?:(?:text|application)\/(?:x-)?(?:java|ecma)script|text\/(?:javascript1\.[0-5]|jscript|livescript))$/i;

const isClassicScript = (script: HTMLScriptElement) => {
  const type = script.getAttribute('type')?.trim();
  return !type || JAVASCRIPT_TYPE.test(type);
};

// A parsed page waits only on a classic external script without `async` or
// `defer`: a vendor snippet that loads its library `async` defines the
// library's stub in the next inline script, and other trackers call that stub
// straight away. A consent-gated `type="text/plain"` or a `nomodule` script
// is never fetched, so it fires neither load nor error.
const blocksParser = (script: HTMLScriptElement) =>
  Boolean(script.getAttribute('src')) &&
  isClassicScript(script) &&
  !script.hasAttribute('nomodule') &&
  !script.hasAttribute('async') &&
  !script.hasAttribute('defer');

// A vendor host that stalls would otherwise hold back the rest of the tracker
// until the browser's network timeout, which can take minutes.
const SCRIPT_LOAD_TIMEOUT_MS = 10_000;

const copyScript = (source: HTMLScriptElement, trackerName?: string) => {
  const script = document.createElement('script');
  script.textContent = source.textContent;

  if (trackerName && trackerName.trim().length > 0) {
    script.setAttribute('data-tracker-name', trackerName);
  }

  source.getAttributeNames().forEach(attribute => {
    const value = source.getAttribute(attribute);

    if (value) {
      script.setAttribute(attribute, value);
    }
  });

  return script;
};

export const TrackerJs: React.VFC<TrackerJsProps> = ({
  customJs,
  location,
  trackerName,
  forceHeadOnly,
}) => {
  const customJsDiv = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = customJsDiv.current;

    if (!customJs || !container) {
      return undefined;
    }

    const div = document.createElement('div');
    div.innerHTML = customJs;
    const sourceNodes = Array.from(div.childNodes);
    const parent =
      (location === 'head' && document.head) ||
      (location === 'body' && document.body) ||
      null;
    const appended: Node[] = [];
    let timeout: number | undefined;
    let cancelled = false;

    // A parsed page runs each script in document order and waits for a
    // parser-blocking external one to load before the next. A created script
    // element loads async, so the next node after such a script is appended
    // only after its load, its error, or the timeout, whichever comes first.
    const appendFrom = (start: number) => {
      for (let i = start; i < sourceNodes.length && !cancelled; i += 1) {
        const source = sourceNodes[i];
        let node: Node = source;

        if (source instanceof HTMLScriptElement) {
          node = copyScript(source, trackerName);
        }

        appended.push(node);
        if (!parent) continue;

        parent.appendChild(node);

        // The source keeps a valueless `async` or `defer`; the copy drops it.
        if (
          node instanceof HTMLScriptElement &&
          source instanceof HTMLScriptElement &&
          blocksParser(source)
        ) {
          const script = node;
          const next = () => {
            window.clearTimeout(timeout);
            script.removeEventListener('load', next);
            script.removeEventListener('error', next);
            appendFrom(i + 1);
          };
          script.addEventListener('load', next);
          script.addEventListener('error', next);
          timeout = window.setTimeout(next, SCRIPT_LOAD_TIMEOUT_MS);
          return;
        }
      }
    };

    appendFrom(0);

    container.innerHTML = forceHeadOnly
      ? ''
      : sourceNodes
          .filter(node => node.nodeName === 'SCRIPT')
          .map(node => (node as HTMLScriptElement).outerHTML)
          .join('');

    return () => {
      cancelled = true;
      window.clearTimeout(timeout);
      appended.forEach(node => {
        node.parentNode?.removeChild(node);
      });
    };
  }, [customJs, location, trackerName, forceHeadOnly]);

  return <div ref={customJsDiv} />;
};
